import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { validatePhysicianField } from "./physicianopportunity.helper";
import { stripTrailingChars } from "../../../utils/formatUtils";

const cleanFaxVal = (val) => {
  if (!val) return "";
  let trimmed = String(val).trim();
  trimmed = trimmed
    .replace(/^(?:Physician\s+fax|fax\s+number|fax)\s+/i, "")
    .trim();
  const changedMatch = /changed\s+(?:from\s+.{0,500}?to\s+|to\s+)(.+)/i.exec(
    trimmed,
  );
  if (changedMatch) {
    trimmed = changedMatch[1].trim();
  }
  trimmed = trimmed.replace(/^["']|["']$/g, "").trim();
  trimmed = trimmed.replace(/^is\s+/i, "").trim();
  trimmed = stripTrailingChars(trimmed, ".,;").trim();
  if (/^(?:is\s+)?(?:unavailable|unavailbale)[.,;]*$/i.test(trimmed)) {
    return "";
  }
  return trimmed;
};

const PhysicianOpportunity = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  const [formData, setFormData] = useState(() => ({
    physicianFirstName:
      data.physicianFirstName ||
      opportunity?.providerDetails?.firstName ||
      opportunity?.physicianFirstName ||
      "",
    physicianLastName:
      data.physicianLastName ||
      opportunity?.providerDetails?.lastName ||
      opportunity?.physicianLastName ||
      "",
    npi:
      data.npi || opportunity?.providerDetails?.npi || opportunity?.npi || "",
    dea:
      data.dea || opportunity?.providerDetails?.dea || opportunity?.dea || "",
    practiceClinic:
      data.practiceClinic ||
      opportunity?.pharmacy ||
      opportunity?.practiceClinic ||
      "",
    phone:
      data.phone ||
      data.physicianPhone ||
      opportunity?.providerDetails?.phone ||
      opportunity?.providerDetails?.phoneNumber ||
      opportunity?.phone ||
      opportunity?.physicianPhone ||
      "",
    fax: cleanFaxVal(
      data.fax ||
        data.physicianFax ||
        opportunity?.providerDetails?.fax ||
        opportunity?.providerDetails?.faxNumber ||
        opportunity?.fax ||
        opportunity?.physicianFax ||
        "",
    ),
  }));

  // Sync initial data back to parent if it was empty
  useEffect(() => {
    if (!data.physicianFirstName && onDataChange) {
      onDataChange(formData);
    }
  }, [data.physicianFirstName, formData, onDataChange]);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    // Initial validation
    const fields = [
      "physicianFirstName",
      "physicianLastName",
      "npi",
      "phone",
      "fax",
    ];
    const initialErrors = {};
    fields.forEach((field) => {
      const error = validatePhysicianField(field, formData[field]);
      if (error) initialErrors[field] = error;
    });
    setErrors(initialErrors);
  }, [formData]);

  const validateField = (name, value) => {
    const error = validatePhysicianField(name, value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      onDataChange(next);
      return next;
    });
    validateField(name, value);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const getErrorProps = (name) => {
    const isInvalid = !!errors[name];
    const shouldShow = touched[name] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[name] : "",
    };
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}>
        Prescribing Physician - From Opportunity
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Physician First Name<span style={{ color: "#E53935" }}>*</span>
            </Typography>
            <TextField
              name="physicianFirstName"
              placeholder="Enter physician first name"
              variant="outlined"
              size="small"
              value={formData.physicianFirstName}
              onChange={(e) => {
                if (e.target.value.length <= 60) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("physicianFirstName")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.physicianFirstName || "").length}/60
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: getErrorProps("physicianFirstName").error
                      ? "#d32f2f"
                      : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                  pr: 0,
                },
              }}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Physician Last Name<span style={{ color: "#E53935" }}>*</span>
            </Typography>
            <TextField
              name="physicianLastName"
              placeholder="Enter physician last name"
              variant="outlined"
              size="small"
              value={formData.physicianLastName}
              onChange={(e) => {
                if (e.target.value.length <= 60) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("physicianLastName")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.physicianLastName || "").length}/60
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: getErrorProps("physicianLastName").error
                      ? "#d32f2f"
                      : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                  pr: 0,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              NPI<span style={{ color: "#E53935" }}>*</span>
            </Typography>
            <TextField
              name="npi"
              placeholder="Enter NPI"
              variant="outlined"
              size="small"
              value={formData.npi}
              onChange={(e) => {
                if (e.target.value.length <= 10) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("npi")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.npi || "").length}/10
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: getErrorProps("npi").error
                      ? "#d32f2f"
                      : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                  pr: 0,
                },
              }}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              DEA #
            </Typography>
            <TextField
              name="dea"
              placeholder="Enter DEA number"
              variant="outlined"
              size="small"
              value={formData.dea}
              onChange={(e) => {
                if (e.target.value.length <= 10) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("dea")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.dea || "").length}/10
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: getErrorProps("dea").error
                      ? "#d32f2f"
                      : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                  pr: 0,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
        >
          Practice Clinic
        </Typography>
        <TextField
          name="practiceClinic"
          placeholder="Enter practice clinic"
          variant="outlined"
          size="small"
          value={formData.practiceClinic}
          onChange={(e) => {
            if (e.target.value.length <= 32) handleChange(e);
          }}
          onBlur={handleBlur}
          {...getErrorProps("practiceClinic")}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                      mr: 0.5,
                    }}
                  >
                    {(formData.practiceClinic || "").length}/32
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: getErrorProps("practiceClinic").error
                  ? "#d32f2f"
                  : "#E8E8E9",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              py: 1.5,
              color: "#0e1324",
              pr: 0,
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Phone Number
            </Typography>
            <TextField
              name="phone"
              placeholder="Enter phone number"
              variant="outlined"
              size="small"
              value={formData.phone || ""}
              onChange={(e) => {
                if (e.target.value.length <= 15) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("phone")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.phone || "").length}/15
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: getErrorProps("phone").error
                      ? "#d32f2f"
                      : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                  pr: 0,
                },
              }}
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
            >
              Fax Number
            </Typography>
            <TextField
              name="fax"
              placeholder="Enter fax number"
              variant="outlined"
              size="small"
              value={formData.fax || ""}
              onChange={(e) => {
                if (e.target.value.length <= 15) handleChange(e);
              }}
              onBlur={handleBlur}
              {...getErrorProps("fax")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          whiteSpace: "nowrap",
                          mr: 0.5,
                        }}
                      >
                        {(formData.fax || "").length}/15
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: getErrorProps("fax").error
                      ? "#d32f2f"
                      : "#E8E8E9",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  py: 1.5,
                  color: "#0e1324",
                  pr: 0,
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

PhysicianOpportunity.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default PhysicianOpportunity;
